// Tax.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, X, Edit2, Trash2 } from "lucide-react";
const API = process.env.REACT_APP_API_URL;
const API_BASE = `${API}/api/admin/tax`;

const Tax = () => {
  const [taxes, setTaxes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTax, setEditTax] = useState(null);

  const [newTax, setNewTax] = useState({
    taxName: "",
    percentage: "",
    description: "",
  });

  // Fetch taxes
  useEffect(() => {
    fetchTaxes();
  }, []);

  const fetchTaxes = async () => {
    try {
      const res = await axios.get(API_BASE);
      setTaxes(res.data);
    } catch (err) {
      console.error("Failed to fetch taxes", err);
    }
  };

  // Add Tax
  const handleAddTax = async (e) => {
    e.preventDefault();
    if (!newTax.taxName || !newTax.percentage) return;

    try {
      await axios.post(API_BASE, newTax, {
        headers: { "Content-Type": "application/json" },
      });
      await fetchTaxes();
      setNewTax({ taxName: "", percentage: "", description: "" });
      setShowModal(false);
    } catch (err) {
      console.error("Failed to add tax", err);
    }
  };

  // Edit Tax
  const handleEditTax = async (e) => {
    e.preventDefault();
    if (!editTax?.id) return;

    try {
      await axios.put(`${API_BASE}/${editTax.id}`, editTax, {
        headers: { "Content-Type": "application/json" },
      });
      await fetchTaxes();
      setEditTax(null);
    } catch (err) {
      console.error("Failed to update tax", err);
    }
  };

  // Delete Tax
  const handleDeleteTax = async (id) => {
    if (!window.confirm("Delete this tax?")) return;
    try {
      await axios.delete(`${API_BASE}/${id}`);
      await fetchTaxes();
    } catch (err) {
      console.error("Failed to delete tax", err);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-text">Tax</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center bg-primary text-surface px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors shadow-sm font-medium"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Tax
        </button>
      </div>

      {/* Tax Table */}
      <div className="bg-surface rounded-xl shadow-sm border border-border overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-textMuted">#</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-textMuted">Tax Name</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-textMuted">Rate (%)</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-textMuted">Description</th>
              <th className="px-6 py-3 text-right text-sm font-medium text-textMuted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {taxes.map((tax, idx) => (
              <tr key={tax.id} className="hover:bg-background/60 transition-colors">
                <td className="px-6 py-4 text-textMuted">{idx + 1}</td>
                <td className="px-6 py-4 font-medium text-text">{tax.taxName}</td>
                <td className="px-6 py-4 font-medium text-text">{tax.percentage}%</td>
                <td className="px-6 py-4 text-textMuted">{tax.description}</td>
                <td className="px-6 py-4 text-right space-x-3">
                  <button
                    onClick={() => setEditTax(tax)}
                    className="text-primary hover:text-primary/80 transition-colors"
                  >
                    <Edit2 className="inline w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTax(tax.id)}
                    className="text-red-500 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="inline w-4 h-4" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {taxes.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center py-4 text-textMuted">
                  No tax records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Tax Modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)} title="Add Tax">
          <form onSubmit={handleAddTax} className="space-y-4">
            <input
              type="text"
              placeholder="Tax Name"
              value={newTax.taxName}
              onChange={(e) => setNewTax({ ...newTax, taxName: e.target.value })}
              className="w-full p-2 border border-border bg-surface text-text rounded focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              type="number"
              placeholder="Rate (%)"
              value={newTax.percentage}
              onChange={(e) =>
                setNewTax({ ...newTax, percentage: e.target.value })
              }
              className="w-full p-2 border border-border bg-surface text-text rounded focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <textarea
              placeholder="Description"
              value={newTax.description}
              onChange={(e) =>
                setNewTax({ ...newTax, description: e.target.value })
              }
              className="w-full p-2 border border-border bg-surface text-text rounded focus:outline-none focus:ring-2 focus:ring-primary"
            ></textarea>
            <button
              type="submit"
              className="w-full bg-primary text-surface px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors shadow-sm font-medium"
            >
              Save
            </button>
          </form>
        </Modal>
      )}

      {/* Edit Tax Modal */}
      {editTax && (
        <Modal onClose={() => setEditTax(null)} title="Edit Tax">
          <form onSubmit={handleEditTax} className="space-y-4">
            <input
              type="text"
              value={editTax.taxName}
              onChange={(e) =>
                setEditTax({ ...editTax, taxName: e.target.value })
              }
              className="w-full p-2 border border-border bg-surface text-text rounded focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              type="number"
              value={editTax.percentage}
              onChange={(e) =>
                setEditTax({ ...editTax, percentage: e.target.value })
              }
              className="w-full p-2 border border-border bg-surface text-text rounded focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <textarea
              value={editTax.description}
              onChange={(e) =>
                setEditTax({ ...editTax, description: e.target.value })
              }
              className="w-full p-2 border border-border bg-surface text-text rounded focus:outline-none focus:ring-2 focus:ring-primary"
            ></textarea>
            <button
              type="submit"
              className="w-full bg-primary text-surface px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors shadow-sm font-medium"
            >
              Update
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
};

// Reusable Modal
const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-surface rounded-lg shadow-lg w-full max-w-md p-6 relative border border-border">
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-textMuted hover:text-text"
      >
        <X className="w-5 h-5" />
      </button>
      <h2 className="text-xl font-semibold mb-4 text-text">{title}</h2>
      {children}
    </div>
  </div>
);

export default Tax;
